import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, ViewEncapsulation } from '@angular/core';
import { MetadataEntityFieldData } from '@jms/model';
import { SharingLabelComponent } from '@workbuddy/ui/fields/sharing.pro/read/sharing.label.component';
import { ButtonMenuAlign, ButtonOverlayComponent, ButtonOverlayContentDirective, ButtonSwitchComponent } from '@workbuddy/ui/v2/components/button';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { CustomFieldRendererInputs, FieldBinding, FieldMode, injectSurfaceContext, SurfaceCapabilityContext, SurfaceEvent } from '@workbuddy/ui/v2/surface.engine';
import { chooseShareParent } from '../share.parent';
import { applyShareToggle, isShareToggleOn, saveShareValue, ShareToggle, shareMenuRows, type ShareValue } from '../share.value';
import { sharingFieldRendererBridgeStyles, sharingFieldRendererStyleId } from './sharing.field.renderer.theme';

/** `sharing.editor`: the header share control — current sharing as the trigger, the toggles the parent offers in its menu. */
@Component({
  selector: 'sharing-field-renderer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [ButtonOverlayComponent, ButtonOverlayContentDirective, ButtonSwitchComponent, SharingLabelComponent],
  templateUrl: 'sharing.field.renderer.component.html',
  styleUrls: ['sharing.field.renderer.component.less'],
})
export class SharingFieldRendererComponent implements CustomFieldRendererInputs {

  private readonly document = inject(DOCUMENT);
  private readonly page = injectSurfaceContext({ optional: true });

  public readonly $field = input<FieldBinding<unknown> | undefined>(undefined, { alias: 'field' });
  public readonly $metadata = input<MetadataEntityFieldData | undefined>(undefined, { alias: 'metadata' });
  public readonly $surfaceMode = input<FieldMode>('edit', { alias: 'surfaceMode' });
  public readonly $capabilityContext = input<SurfaceCapabilityContext | undefined>(undefined, { alias: 'capabilityContext' });

  protected readonly menuAlignment = ButtonMenuAlign.Right;
  protected readonly $value = computed<ShareValue>(() => saveShareValue(this.$field()?.$value() as ShareValue | null | undefined));
  protected readonly $readonly = computed(() => this.$surfaceMode() === 'read' || !!this.$field()?.$readonly() || !!this.$field()?.$disabled());
  protected readonly $rows = computed(() => {
    const context = this.$capabilityContext();
    return shareMenuRows(context ? chooseShareParent(context.entity, context.parents)?.options ?? null : null);
  });

  public constructor() {

    DesignSystem.Mount(this.document, sharingFieldRendererStyleId, sharingFieldRendererBridgeStyles);
  }

  protected isOn(toggle: ShareToggle): boolean {

    return isShareToggleOn(this.$value(), toggle);
  }

  protected isLocked(toggle: ShareToggle): boolean {

    return toggle !== ShareToggle.System && this.$value().systemSharing;
  }

  protected onToggle(toggle: ShareToggle, on: boolean): void {

    const field = this.$field() as FieldBinding<ShareValue> | undefined;
    if (!field || this.$readonly()) return;

    const next = applyShareToggle(this.$value(), toggle, on);
    field.setValue(next);
    field.markTouched();
    // A header control never blurs, so a patch surface saves only on this commit.
    this.page?.eventBus.emit(SurfaceEvent.FieldCommit, { bind: field.bind, value: next });
  }
}
